import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft } from "lucide-react";
import {
  approveProviderCode,
  approveRequest,
  checkProviderCode,
  declineRequest,
  getCareTeam,
  stopSharing,
} from "../../../services/CareTeamService";
import {
  CODE_LENGTH,
  codeErrorText,
  formatLongDate,
  providerLine,
  sanitizeCode,
  shortName,
} from "../careUtils";
import { Dialog, Plate, StatusBadge, Toast, icon, useToast } from "../ui";
import { WILDFLOWER_PATH } from "../art";
import ConsentDetails from "./ConsentDetails";
import "../careTeam.css";

const ConsentScreen = ({ provider, onApprove, onDecline, onBack, busy }) => (
  <div className="ct-player__inner ct-player__inner--consent">
    <button
      type="button"
      className="ct-link-button ct-back"
      style={{ textDecoration: "none" }}
      onClick={onBack}
    >
      {icon(ChevronLeft, 22)}
      Back
    </button>
    <h1>Share your progress with {shortName(provider.name)}?</h1>
    <ConsentDetails provider={provider} />
    <div className="ct-consent-actions">
      <button
        type="button"
        className="ct-btn ct-btn--primary"
        onClick={onApprove}
        disabled={busy}
      >
        Yes, share my progress
      </button>
      <button
        type="button"
        className="ct-btn ct-btn--secondary"
        onClick={onDecline}
        disabled={busy}
      >
        No, thank you
      </button>
    </div>
  </div>
);

// /settings/care-team
const CareTeamPage = () => {
  const [searchParams] = useSearchParams();
  const [team, setTeam] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [code, setCode] = useState(() => sanitizeCode(searchParams.get("code") || ""));
  const [codeError, setCodeError] = useState(null);
  // { provider, code } for a code, or { provider, linkId } for a pending request.
  const [consent, setConsent] = useState(null);
  const [stopping, setStopping] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, showToast] = useToast();

  const load = useCallback(async () => {
    try {
      setTeam(await getCareTeam());
      setLoadError(false);
    } catch (err) {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitCode = async () => {
    if (code.length !== CODE_LENGTH) {
      setCodeError({ error: "format" });
      return;
    }
    setBusy(true);
    try {
      const { provider } = await checkProviderCode(code);
      setConsent({ provider, code });
      setCodeError(null);
      window.scrollTo?.(0, 0);
    } catch (err) {
      setCodeError(err?.response?.data?.error ? err.response.data : { error: "not_found" });
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      if (consent.code) await approveProviderCode(consent.code);
      else await approveRequest(consent.linkId);
      showToast(`You're now sharing with ${consent.provider.name}`);
      setConsent(null);
      setCode("");
      await load();
    } catch (err) {
      if (err?.response?.data?.error) {
        // The code changed state in the meantime (expired, already used…).
        setCodeError(err.response.data);
        setConsent(null);
      } else {
        showToast("That didn't work. Please try again.", { error: true });
      }
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      if (consent.linkId) {
        await declineRequest(consent.linkId);
        await load();
      }
      setConsent(null);
      setCode("");
      showToast("Request declined. Nothing was shared.");
    } catch (err) {
      showToast("That didn't work. Please try again.", { error: true });
    } finally {
      setBusy(false);
    }
  };

  const confirmStop = async () => {
    const link = stopping;
    setStopping(null);
    try {
      await stopSharing(link.id);
      showToast(`You've stopped sharing with ${link.provider.name}`);
      await load();
    } catch (err) {
      showToast("That didn't work. Please try again.", { error: true });
    }
  };

  const connected = team?.connected || [];
  const pending = team?.pending || [];
  const empty = team && !connected.length && !pending.length;

  return (
    <div className="ct ct-player">
      <Helmet>
        <title>My care team – Mend Grove</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {consent ? (
        <ConsentScreen
          provider={consent.provider}
          busy={busy}
          onApprove={approve}
          onDecline={decline}
          onBack={() => setConsent(null)}
        />
      ) : (
        <div className="ct-player__inner">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Link to="/settings" className="ct-back">
              {icon(ChevronLeft, 22)}
              Settings
            </Link>
            <h1>My care team</h1>
            <p className="ct-intro">
              You can choose to share your game progress with a therapist or doctor.
              This is completely optional.
            </p>
          </div>

          {loadError && (
            <p className="ct-code-error" role="alert">
              {icon(AlertTriangle, 22)}
              <span>We couldn't load your care team. Please refresh the page.</span>
            </p>
          )}

          {empty && (
            <div className="ct-player-empty">
              <Plate
                label="Illustration: a quiet path through spring wildflowers"
                art={WILDFLOWER_PATH}
              />
              <h2>Just you and your games</h2>
              <p>
                MendGrove works perfectly on its own. If a provider ever asks to follow
                your progress, they'll send you an invite link or give you a code to
                enter below.
              </p>
            </div>
          )}

          {pending.length > 0 && (
            <section className="ct-section">
              <h2>Waiting for you</h2>
              {pending.map((link) => (
                <div key={link.id} className="ct-provider-card ct-provider-card--pending">
                  <span className="ct-provider-card__name">{link.provider.name}</span>
                  <span className="ct-provider-card__role">
                    {providerLine(link.provider)}
                  </span>
                  <span className="ct-provider-card__note">Asked to see your progress</span>
                  <button
                    type="button"
                    className="ct-btn ct-btn--primary"
                    onClick={() => {
                      setConsent({ provider: link.provider, linkId: link.id });
                      window.scrollTo?.(0, 0);
                    }}
                  >
                    Review request
                  </button>
                </div>
              ))}
            </section>
          )}

          {connected.length > 0 && (
            <section className="ct-section">
              <h2>Sharing with</h2>
              {connected.map((link) => (
                <div key={link.id} className="ct-provider-card">
                  <div className="ct-provider-card__head">
                    <span className="ct-provider-card__name">{link.provider.name}</span>
                    <StatusBadge status="active" label="Sharing" />
                  </div>
                  <span className="ct-provider-card__role">
                    {providerLine(link.provider)}
                  </span>
                  <span className="ct-provider-card__note">
                    {link.since
                      ? `Sharing since ${formatLongDate(link.since)}`
                      : "Sharing since today"}
                  </span>
                  <button
                    type="button"
                    className="ct-btn ct-btn--danger"
                    onClick={() => setStopping(link)}
                  >
                    Stop sharing
                  </button>
                </div>
              ))}
            </section>
          )}

          <section className="ct-code-section">
            <h2>Have a code from a provider?</h2>
            <label htmlFor="ct-provider-code">Enter provider code</label>
            <input
              id="ct-provider-code"
              className="ct-code-input"
              value={code}
              onChange={(e) => {
                setCode(sanitizeCode(e.target.value));
                setCodeError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && submitCode()}
              maxLength={CODE_LENGTH}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-invalid={!!codeError}
              aria-describedby="ct-provider-code-help"
              placeholder="K7Q2MX"
            />
            {codeError && (
              <div className="ct-code-error" role="alert">
                {icon(AlertTriangle, 18)}
                <span>{codeErrorText(codeError)}</span>
              </div>
            )}
            <span id="ct-provider-code-help" className="ct-code-help">
              6 letters or numbers. Capitals don't matter.
            </span>
            <button
              type="button"
              className="ct-btn ct-btn--primary"
              onClick={submitCode}
              disabled={busy}
            >
              Continue
            </button>
          </section>
        </div>
      )}

      {stopping && (
        <Dialog
          role="alertdialog"
          labelledBy="ct-stop-title"
          className="ct-sheet"
          backdropClassName="ct-backdrop ct-backdrop--sheet"
          onClose={() => setStopping(null)}
        >
          <h2 id="ct-stop-title">Stop sharing with {stopping.provider.name}?</h2>
          <p>
            They won't see any new games you play, and they can no longer change your
            game settings.
          </p>
          <p>
            They may keep what they've already seen in their records. You can share
            again later with a new code.
          </p>
          <button type="button" className="ct-btn ct-btn--danger" onClick={confirmStop}>
            Stop sharing
          </button>
          <button
            type="button"
            className="ct-btn ct-btn--primary"
            onClick={() => setStopping(null)}
          >
            Keep sharing
          </button>
        </Dialog>
      )}
      <Toast toast={toast} player />
    </div>
  );
};

export default CareTeamPage;
